import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import theme from 'components/themeProvider/theme'
import { OutlineBadge, ErrorHandler, LoadingSpinner } from 'components'
import {
	SystemApprovalStatus,
	TenantType,
	SoftwareSystemApprovalsQuery,
	SoftwareSystemApprovalsQueryVariables
} from 'generated/types'
import { SOFTWARE_SYSTEM_APPROVALS } from 'modules/core/queries'
import {
	ApprovalsContainer,
	ApprovalItem,
	BadgeZIndexWrapper,
	ApprovalStatusLabel
} from '../styles'

// Mapping from system approval status to background colors used in the inline labels.
const SYSTEM_APPROVAL_COLOR: Record<SystemApprovalStatus, string> = {
	APPROVED: theme.approvalGreenLight,
	APPROVED_WITH_RESERVATION: theme.approvalOrangeLight,
	REJECTED: theme.approvalRedLight
}

type Tenant = TenantType

export type SystemApprovalStatusInlineProps = {
	systemId?: string
	quarter?: string
	year?: string
	catalogTypeCode?: string
	tenantsInGroup?: (Tenant | null | undefined)[]
}

/**
 * Inline visualization of system approval statuses per tenant for the current group (catalog type).
 *
 * This component fetches approvals for the given `systemId`/`quarter`/`year` and then:
 * - optionally filters by `catalogTypeCode`
 * - displays a colored `OutlineBadge` for each tenant with its approval status
 * - lists missing tenants (those in `tenantsInGroup` without approvals) as grey badges
 */
const SystemApprovalStatusInline: React.FC<SystemApprovalStatusInlineProps> = ({
	systemId,
	quarter,
	year,
	catalogTypeCode,
	tenantsInGroup = []
}) => {
	const [t] = useTranslation()
	const parseQuarter = (q?: string) => {
		const m = q?.match(/^(\d{4})-Q([1-4])$/)
		if (!m) return null
		return { year: Number(m[1]), quarter: Number(m[2]) }
	}

	const parsed = parseQuarter(quarter)
	const yearNum = Number(year)

	const quarterNum = parsed?.quarter
	const finalYear = Number.isFinite(yearNum) ? yearNum : parsed?.year

	const shouldSkip =
		!systemId ||
		!quarterNum ||
		!finalYear ||
		Number.isNaN(quarterNum) ||
		Number.isNaN(finalYear)

	const { data, error, loading } = useQuery<
		SoftwareSystemApprovalsQuery,
		SoftwareSystemApprovalsQueryVariables
	>(SOFTWARE_SYSTEM_APPROVALS, {
		variables: {
			systemId: systemId ?? '',
			quarter: quarterNum ?? 0,
			year: finalYear ?? 0,
			catalogTypeCode
		},
		skip: shouldSkip
	})

	const approvals = (data?.softwareSystem?.approvals ?? []).filter(
		(a): a is NonNullable<typeof a> => Boolean(a)
	)

	// Server-side filtering by catalogTypeCode (if provided) is already applied via the GraphQL query.
	// Here we only filter out approvals missing tenant or status information.
	const filteredApprovals = approvals.filter(a => a.tenant && a.status)

	// Determine which tenants already approved something and which tenants are still missing
	const approvedTenantIds = filteredApprovals
		.map(a => a.tenant?.id)
		.filter((id): id is string => Boolean(id))
	const missingTenants = tenantsInGroup.filter((tenant): tenant is Tenant =>
		Boolean(tenant && !approvedTenantIds.includes(tenant.id))
	)

	if (loading) {
		return <LoadingSpinner />
	}
	return (
		<ErrorHandler error={error}>
			<ApprovalsContainer>
				{filteredApprovals.map(systemApproval => {
					const tenant = systemApproval.tenant
					if (!tenant) return null
					const statusKey = systemApproval.status as SystemApprovalStatus
					return (
						<ApprovalItem key={`approved-${tenant.id}`}>
							<BadgeZIndexWrapper>
								<OutlineBadge color={tenant.hexColor}>
									{tenant.name}
								</OutlineBadge>
							</BadgeZIndexWrapper>
							<ApprovalStatusLabel bgColor={SYSTEM_APPROVAL_COLOR[statusKey]}>
								{`${t(`approval.systemApprovalStatus.${statusKey}`)}`}
							</ApprovalStatusLabel>
						</ApprovalItem>
					)
				})}
				{missingTenants.map(tenant => (
					<ApprovalItem key={`missing-${tenant.id}`}>
						<BadgeZIndexWrapper>
							<OutlineBadge color={theme.approvalGrey}>
								{tenant.name}
							</OutlineBadge>
						</BadgeZIndexWrapper>
						<ApprovalStatusLabel
							bgColor={theme.approvalGreyLight}
							textColor={theme.approvalGrey}
						>
							{`${t('approval.systemApprovalStatus.none')}`}
						</ApprovalStatusLabel>
					</ApprovalItem>
				))}
			</ApprovalsContainer>
		</ErrorHandler>
	)
}

export default SystemApprovalStatusInline
